import type { CustomerCandidate } from '../../../api/customer';

interface CustomerMergeCandidatesProps {
  candidates: CustomerCandidate[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const HIT_LABELS: Record<string, string> = {
  id_card: '身份证',
  phone: '手机号',
  backup_phone: '备用号',
  email: '邮箱',
  full_name: '姓名',
};

/**
 * 合并候选列表：
 * - 显示头像、姓名、联系方式、脱敏身份证号
 * - 高亮命中字段（身份证 > 电话 > 备用 > 邮箱 > 姓名）
 * - 显示订单数量 / 是否注册用户
 * - 单选模式：选中一条作为复用/合并的主档
 */
export default function CustomerMergeCandidates({
  candidates,
  selectedId,
  onSelect,
}: CustomerMergeCandidatesProps) {
  if (!candidates || candidates.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2.5">
      {/* 顶部：熟人核对前置提示 */}
      <div className="flex gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-[12px] leading-relaxed text-amber-800">
        <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
          <i className="ri-shield-user-line" />
        </div>
        <div>
          <div className="font-semibold mb-0.5">合并前请务必完成熟人核对</div>
          <div className="text-amber-700">
            请通过<span className="font-medium">微信 / 电话</span>等真实渠道<span className="font-medium">亲自联系客户本人</span>
            确认该身份确为同一人，避免客户换号、号码回收导致的误合并。
          </div>
        </div>
      </div>

      <div className="text-[11px] text-gray-400 px-1">
        系统匹配到以下可能相同的客户，请选择要复用的主档：
      </div>

      {candidates.map((c) => {
        const selected = c.id === selectedId;
        return (
          <button
            type="button"
            key={c.id}
            onClick={() => onSelect(c.id)}
            className={`w-full text-left rounded-xl border p-3 transition-colors cursor-pointer ${
              selected
                ? 'border-teal-400 bg-teal-50/60 ring-1 ring-teal-300'
                : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}
          >
            <div className="flex items-start gap-3">
              {/* 头像 */}
              <div className="w-11 h-11 shrink-0 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center text-gray-400">
                {c.avatarUrl ? (
                  <img src={c.avatarUrl} alt={c.fullName} className="w-full h-full object-cover" />
                ) : (
                  <i className="ri-user-3-line text-lg" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                {/* 第一行：姓名 + 徽标 */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {c.fullName || '未命名'}
                  </span>
                  {c.isUser ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-emerald-100 text-emerald-700">
                      <i className="ri-shield-check-line" />
                      已注册用户
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-rose-100 text-rose-700">
                      <i className="ri-user-line" />
                      非注册用户
                    </span>
                  )}
                  {c.orderCount > 0 && (
                    <span className="px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-indigo-100 text-indigo-700">
                      订单 {c.orderCount}
                    </span>
                  )}
                  {selected && (
                    <span className="ml-auto text-teal-500 text-xs flex items-center gap-0.5">
                      <i className="ri-check-line" /> 已选
                    </span>
                  )}
                </div>

                {/* 第二行：联系方式（命中字段加下划线高亮） */}
                <div className="mt-1 text-xs text-gray-500 space-y-0.5">
                  <div className="flex flex-wrap gap-x-3">
                    <span className={c.hitFields.includes('phone') ? 'text-amber-600 font-medium underline decoration-amber-400' : ''}>
                      手机：{c.phone || '—'}
                    </span>
                    {c.backupPhone && (
                      <span className={c.hitFields.includes('backup_phone') ? 'text-amber-600 font-medium underline decoration-amber-400' : ''}>
                        备用：{c.backupPhone}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-3">
                    {c.idCard && (
                      <span className={c.hitFields.includes('id_card') ? 'text-amber-600 font-medium underline decoration-amber-400' : ''}>
                        证件：{c.idCard}
                      </span>
                    )}
                    {c.email && (
                      <span className={c.hitFields.includes('email') ? 'text-amber-600 font-medium underline decoration-amber-400' : ''}>
                        邮箱：{c.email}
                      </span>
                    )}
                  </div>
                </div>

                {/* 第三行：命中字段 Chips */}
                {c.hitFields.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {c.hitFields.map((f) => (
                      <span
                        key={f}
                        className="px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-amber-100 text-amber-700"
                      >
                        命中：{HIT_LABELS[f] || f}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
